/**
 * Layout constants shared by the ProjectBank scene and the bank picker dialog.
 * Pixel values feed the square card grid and the dense list rows; the batch
 * tunings drive useScrollBatch per view mode.
 */

/** Width of the folder tree pane at sm and up. */
export const TREE_PANE_WIDTH = 320;

/**
 * DOM id of the drawer the folder tree pane lives in on xs
 * (BankSidebarDrawer); the header's opener names it in `aria-controls`.
 */
export const BANK_SIDEBAR_DRAWER_ID = 'project-bank-sidebar-drawer';

/**
 * DOM id of the bottom sheet the AI panel opens in on xs (BankAiSheet); the
 * toolbar's AI button names it in `aria-controls`.
 */
export const BANK_AI_SHEET_ID = 'project-bank-ai-sheet';

/** Minimum column width of the card grid's auto-fill columns (sm+). */
export const CARD_MIN_WIDTH = 150;

/**
 * Minimum column width of the card grid's auto-fill columns on xs: the
 * grid's own xs rule, which the bank's grids replace with their phone layout
 * (CARD_GRID_COLUMNS_XS columns) and the bank picker dialog's grid keeps
 * (BankItemGrid `autoFillXs`: it sits inside a dialog and never spans a
 * phone's width).
 */
export const CARD_MIN_WIDTH_XS = 110;

/**
 * Column count of the bank's card grid on xs: three equal columns whatever
 * the phone's width, so the cards grow and shrink with the screen instead of
 * the grid falling back to two columns on a narrow phone.
 */
export const CARD_GRID_COLUMNS_XS = 3;

/** Gap between the cards of the grid (sm+, and the picker's grid on xs). */
export const CARD_GRID_GAP = 8;

/** Gap between the cards of the bank's grid on xs. */
export const CARD_GRID_GAP_XS = 4;

/** Padding around the card grid (sm+, and the picker's grid on xs). */
export const CARD_GRID_PADDING = 12;

/**
 * Top and bottom padding of the bank's card grid on xs. It has no side
 * padding there, so the three cards take the whole width of the content pane.
 */
export const CARD_GRID_PADDING_XS = 4;

/**
 * Size of the format glyph a card shows in place of a thumbnail (sm+, and
 * the picker's cards on xs).
 */
export const CARD_GLYPH_SIZE = 44;

/** Size of the card's format glyph in the bank's grid on xs. */
export const CARD_GLYPH_SIZE_XS = 36;

/**
 * Height of a card's glyph area as a fraction of the card (sm+, and the
 * picker's cards on xs): the glyph is centred in the upper part because the
 * info overlay covers the bottom.
 */
export const CARD_GLYPH_AREA = '60%';

/**
 * Height of the card's glyph area in the bank's grid on xs, where a card is
 * a third of the content pane wide and the info overlay covers a larger
 * share of it, so the glyph is centred higher. From a phone about 350px wide
 * up it stays clear of every card's overlay; at 320px the overlay of a file
 * card that shows its name and its size covers the glyph's lower edge by
 * about 7px.
 */
export const CARD_GLYPH_AREA_XS = '50%';

/**
 * Grid card outer height (sm+).
 * @deprecated Cards are square since the layout redesign (their height
 * follows the column width); kept for callers that still size by it.
 */
export const CARD_HEIGHT = 240;

/**
 * Grid card outer height on xs.
 * @deprecated See CARD_HEIGHT.
 */
export const CARD_HEIGHT_XS = 228;

/**
 * Height of the card's media well (sm+).
 * @deprecated The media well fills the whole card since the layout redesign;
 * kept for callers that still size by it.
 */
export const CARD_MEDIA_HEIGHT = 112;

/**
 * Height of the card's media well on xs.
 * @deprecated See CARD_MEDIA_HEIGHT.
 */
export const CARD_MEDIA_HEIGHT_XS = 100;

/** List row height (sm+). */
export const ROW_HEIGHT = 40;

/** List row height on xs. */
export const ROW_HEIGHT_XS = 36;

/** useScrollBatch tuning for the card grid. */
export const GRID_BATCH = { initial: 100, step: 50, threshold: 200 };

/** useScrollBatch tuning for the list view. */
export const LIST_BATCH = { initial: 150, step: 100, threshold: 300 };

/** localStorage key of the remembered grid/list view mode. */
export const VIEW_MODE_STORAGE_KEY = 'lixani.projectBank.viewMode';

/**
 * localStorage key of the gallery view's own remembered view mode (grid,
 * list or its floorplan map, see hooks/useBankMapView). Until the gallery
 * view has one, it opens in the bank's grid / list; 'map' never goes into
 * VIEW_MODE_STORAGE_KEY, which the whole bank reads.
 */
export const GALLERY_VIEW_MODE_STORAGE_KEY =
  'lixani.projectBank.galleryViewMode';

/** localStorage key of the remembered folder-tree open state. */
export const TREE_OPEN_STORAGE_KEY = 'lixani.projectBank.treeOpen';

/**
 * localStorage key of the remembered AI panel open state. Not `aiPanelOpen`:
 * that key was written `true` on every visit while the panel opened by
 * default, so it cannot tell a user who left it open from one who never chose.
 */
export const AI_PANEL_OPEN_STORAGE_KEY = 'lixani.projectBank.aiPanelKeptOpen';

/**
 * The page's side gutter on xs: the side padding of the header band and of
 * the main column. The drawer's swipe strip (BankSidebarDrawer) lies over the
 * left edge of the page and swallows every tap that lands on it, so the xs
 * padding and the strip must both equal this value: a strip wider than the
 * gutter takes the taps of the controls and the cards that start after it.
 */
export const BANK_XS_GUTTER = 8;
